const normalizeDownloadUrl = (urlOrPath) => {
  if (!urlOrPath) return null;
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;

  const base = process.env.REACT_APP_CLOUDFRONT_URL?.replace(/\/$/, "") || "";
  const path = String(urlOrPath).replace(/^\//, "");
  return base ? `${base}/${path}` : urlOrPath;
};

const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url);

const triggerAnchorDownload = (url, fileName = "download", { openInNewTab = false } = {}) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener noreferrer";
  if (openInNewTab) {
    link.target = "_blank";
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const checkDownloadReady = async (url) => {
  try {
    const headResponse = await fetch(url, { method: "HEAD" });
    return {
      ready: headResponse.ok,
      sawHttpResponse: true,
    };
  } catch {
    // HEAD may be blocked by CORS; fall back to a tiny ranged GET below.
  }

  try {
    const rangeResponse = await fetch(url, {
      method: "GET",
      headers: { Range: "bytes=0-0" },
    });
    return {
      ready: rangeResponse.ok || rangeResponse.status === 206,
      sawHttpResponse: true,
    };
  } catch {
    return {
      ready: false,
      sawHttpResponse: false,
    };
  }
};

export const triggerFileDownload = (urlOrPath, fileName = "download") => {
  const url = normalizeDownloadUrl(urlOrPath);
  if (!url) return;
  triggerAnchorDownload(url, fileName);
};

/**
 * Download a file from a URL or pre-signed S3 link.
 * Uses fetch+blob for absolute URLs so the browser respects the filename.
 */
export const downloadFromUrl = async (urlOrPath, fileName = "download") => {
  const url = normalizeDownloadUrl(urlOrPath);
  if (!url) {
    throw new Error("Download failed: no URL returned");
  }

  if (isAbsoluteUrl(url)) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerAnchorDownload(objectUrl, fileName);
      URL.revokeObjectURL(objectUrl);
      return;
    } catch {
      // CORS may block fetch on S3; open the pre-signed URL directly instead.
      triggerAnchorDownload(url, fileName, { openInNewTab: true });
      return;
    }
  }

  triggerAnchorDownload(url, fileName);
};

export const waitForDownloadReady = async (
  urlOrPath,
  { maxAttempts = 30, intervalMs = 2000 } = {}
) => {
  const url = normalizeDownloadUrl(urlOrPath);
  if (!url) return false;

  // Pre-signed URLs are returned only once the export is complete.
  if (isAbsoluteUrl(url)) return true;

  let sawHttpResponse = false;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const { ready, sawHttpResponse: gotResponse } = await checkDownloadReady(url);
    if (gotResponse) sawHttpResponse = true;
    if (ready) return true;

    if (!sawHttpResponse && attempt >= 2) {
      await new Promise((resolve) => setTimeout(resolve, 10000));
      return true;
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  return sawHttpResponse ? false : true;
};

export const downloadFileWhenReady = async (
  urlOrPath,
  fileName = "download",
  waitOptions
) => {
  const url = normalizeDownloadUrl(urlOrPath);
  if (!url) {
    throw new Error("Download failed: no URL returned");
  }

  const isReady = await waitForDownloadReady(url, waitOptions);

  if (!isReady) {
    throw new Error("Download is not ready yet. Please try again in a moment.");
  }

  await downloadFromUrl(url, fileName);
};
